import { useEffect, useState } from "react";
import { DashboardConfig, SavedReportingYear } from "../../types/dashboardConfig";
import * as ToolSettings from "../../settings/toolSettings";
import {
  Period,
  RollingPeriod,
  createReportingYearPeriod,
  createRollingPeriods,
  getAutoMatchedComparison,
  getTodayInTimeZone,
} from "./periods";

interface PeriodOptions {
  reportingYears: Period[];
  rollingPeriods: RollingPeriod[];
  timeZone: string;
}

interface YearOption {
  name: number;
  value: number;
}

const getInitialFilters = (config: DashboardConfig): Record<string, any> => {
  const initialConfigurableFilters: Record<string, any> = {};
  config.configurableFilters.forEach((filter) => {
    initialConfigurableFilters[filter.stateKey] = filter.initialValue;
  });
  return {
    focusYear: null,
    comparisonYear: null,
    // The Time Period picker's dashboards choose periods instead of years
    focusPeriod: null,
    comparisonPeriod: null,
    ...initialConfigurableFilters,
  };
};

const getYearOptions = (reports: SavedReportingYear[]): YearOption[] =>
  reports
    .map((report) => ({ name: report.reportingYear, value: report.reportingYear }))
    .sort((a, b) => b.value - a.value);

// The reporting year that today falls in, or the newest one
const getFocusYear = (reports: SavedReportingYear[], years: YearOption[], now: Date) => {
  const currentReport = reports.find((report) => {
    const rangeFirst = new Date(report.dataRangeFirst);
    const rangeLast = new Date(report.dataRangeLast);
    return now >= rangeFirst && now <= rangeLast;
  });
  return currentReport?.reportingYear ?? years[0]?.value;
};

const getInitialYearFilters = (years: YearOption[], focusYear: number) => {
  const comparisonYear =
    years.find((year) => year.value === focusYear - 1)?.value ?? years[1]?.value;
  return { focusYear, comparisonYear: comparisonYear ?? focusYear };
};

const getPeriodOptions = (reports: SavedReportingYear[], now: Date): PeriodOptions => {
  const groupTimeZone = ToolSettings.getSetting("group", "timeZone");
  return {
    reportingYears: [...reports]
      .sort((a, b) => b.reportingYear - a.reportingYear)
      .map(createReportingYearPeriod),
    rollingPeriods: createRollingPeriods(getTodayInTimeZone(now, groupTimeZone), groupTimeZone),
    timeZone: groupTimeZone,
  };
};

const getInitialPeriodFilters = (periodOptions: PeriodOptions, focusYear: number) => {
  const focusPeriod = periodOptions.reportingYears.find(
    (period) => period.key === `reporting-year-${focusYear}`,
  );
  if (!focusPeriod) throw new Error(`No annual report for reporting year ${focusYear}`);
  return {
    focusPeriod,
    comparisonPeriod: getAutoMatchedComparison(focusPeriod, periodOptions.reportingYears),
  };
};

// What an Insights & Trends dashboard filters by. The year or period filters start empty and are
// filled in once the reporting years have loaded: the current reporting year compared with the one
// before it
export default function useInsightDashboardFilters(config: DashboardConfig) {
  const [filters, setFilters] = useState<Record<string, any>>(() => getInitialFilters(config));
  const [reportingYears, setReportingYears] = useState<YearOption[]>([]);
  const [periodOptions, setPeriodOptions] = useState<PeriodOptions | null>(null);
  const [filterOptions, setFilterOptions] = useState<{ name: string; value: string }[]>([]);

  useEffect(() => {
    const loadInitialData = async () => {
      const reports = await config.getReportingYearsFunction(config.dashboardType);
      const years = getYearOptions(reports);
      setReportingYears(years);
      setFilterOptions(config.getFilterOptionsFunction(config.dashboardType));

      const now = new Date();
      const focusYear = getFocusYear(reports, years, now);

      if (config.timePeriodPicker) {
        const options = getPeriodOptions(reports, now);
        setPeriodOptions(options);
        setFilters((prevFilters) => ({
          ...prevFilters,
          ...getInitialPeriodFilters(options, focusYear),
        }));
        return;
      }

      setFilters((prevFilters) => ({ ...prevFilters, ...getInitialYearFilters(years, focusYear) }));
    };
    loadInitialData();
  }, [config]);

  const hasValidPeriods = config.timePeriodPicker
    ? filters.focusPeriod !== null && filters.comparisonPeriod !== null
    : filters.focusYear !== null && filters.comparisonYear !== null;

  return { filters, setFilters, reportingYears, periodOptions, filterOptions, hasValidPeriods };
}
